// FD-420 / FD-ADR-040 §7 — the console's configuration, resolved at RUNTIME.
//
// Until this file existed every console value was compiled into the bundle:
// `authConfig.ts` read `VITE_CLIENT_ID` / `VITE_TENANT_ID` / `VITE_API_SCOPE` at
// build time and `api/client.ts` baked `VITE_MANAGER_API_BASE_URL` into the
// JavaScript. One build served exactly one environment, which is why
// FD-ADR-040's "one login, many environments" was impossible to write: the
// bundle could not name a second Manager host.
//
// So the values move to a file the host serves — `/config.json` — fetched
// before MSAL, validated, and handed to `buildMsalConfig`. A fleet console and
// a per-environment SWA then run the SAME bundle and differ only in the file
// beside it.
//
// The DEV fallback to `VITE_*` is deliberately narrow — see `loadConsoleConfig`.

/** The only schema version this console understands. */
export const CONSOLE_CONFIG_SCHEMA_VERSION = 1;

/** Where the host serves the file. Root-relative: the console owns its origin. */
export const CONSOLE_CONFIG_URL = '/config.json';

/**
 * One AirBridge environment — one deployment's data plane.
 *
 * A fleet console lists several; a per-environment SWA lists exactly one, which
 * is what keeps Mode 1's UX byte-identical (FD-421 hides the selector at one).
 */
export interface ConsoleEnvironment {
  /** Stable key. FD-421 puts this in `?env=` — never a label, never an index. */
  id: string;
  /**
   * What an operator reads in the selector. OPTIONAL since FD-586.
   *
   * `config.json` is fetched BEFORE sign-in, so everything in it is public — and
   * on the Air Bridge fleet console that made every customer's NAME public to
   * anyone who loaded the page. The generator may now omit it, and the console
   * resolves the display label ONCE, in `EnvironmentProvider`: this value when
   * present, else the label the environment's own Manager reports from
   * `GET /v1/whoami` (which only a permitted tenant can read), else the id.
   * Nothing downstream of the provider sees this optional form — the context
   * hands out environments whose `label` is always a string.
   */
  label?: string;
  /** Absolute origin of that environment's Manager Function App, no trailing slash. */
  managerBaseUrl: string;
  /** FD-ADR-016 deployment identifier. Displayed; never used to route a request. */
  customerEnvironmentId?: string;
  /**
   * Free-form badge tone for the selector. The loader carries it and does not
   * interpret it — the vocabulary belongs to FD-421, which renders it.
   */
  tone?: string;
}

export interface ConsoleConfig {
  schemaVersion: number;
  /** The Entra app registration this console signs in against. */
  clientId: string;
  /**
   * Passed VERBATIM to MSAL (ADR-014 parameterisation 2) — a tenant GUID in
   * Mode 1, `.../organizations` in Mode 2. Never string-built from a tenant id
   * anywhere but the DEV fallback below, which is the whole point of that rule.
   */
  authority: string;
  /** The AdminAccess scope URI. */
  scope: string;
  /**
   * Optional redirect-URI override. Omitted is the normal case: the console is
   * served from the host it redirects back to, so `origin + '/'` is both correct
   * and one less value to keep in step with the Entra app registration.
   */
  redirectUri?: string;
  environments: ConsoleEnvironment[];
  /**
   * FD-592 (FD-ADR-047 §5) — where the AirBridge sign-in helper extension is
   * installed from. OPTIONAL: the store listings are assigned at first upload and
   * a Self-hosted console may have its own install route. Absent, the Activate
   * page says the helper is "available from your administrator" rather than
   * rendering a link that goes nowhere.
   */
  signInHelper?: SignInHelperLinks;
}

/**
 * Where the sign-in helper comes from. Every URL is an absolute https URL when present.
 *
 * FD-608 added the INTERIM self-hosted channel beside the store links, because store
 * review takes up to two weeks (FD-605): `downloadUrl` is a zip served by this
 * console host for a manual "Load unpacked" install, and `forceInstallUpdateUrl` +
 * `extensionId` are what IT puts in `ExtensionInstallForcelist` as `<id>;<url>`.
 * Store links, when present, take precedence in the UI; the download stays as a
 * secondary link. The self-hosted ID differs from the store IDs.
 */
export interface SignInHelperLinks {
  edgeStoreUrl?: string;
  chromeStoreUrl?: string;
  downloadUrl?: string;
  forceInstallUpdateUrl?: string;
  extensionId?: string;
}

const EXTENSION_ID_PATTERN = /^[a-p]{32}$/;

/**
 * A configuration problem stated in terms an operator can act on.
 *
 * Every message names the FIELD, because the failure this class exists to
 * prevent is a console that renders nothing and explains nothing. `BootFailure`
 * prints `message` verbatim.
 */
export class ConsoleConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConsoleConfigError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireString(source: Record<string, unknown>, field: string, where: string): string {
  const value = source[field];
  if (typeof value !== 'string' || value.trim() === '') {
    const what = value === undefined ? 'is missing required field' : 'has an empty or non-string';
    throw new ConsoleConfigError(`${where} ${what} "${field}".`);
  }
  return value.trim();
}

function optionalString(
  source: Record<string, unknown>,
  field: string,
  where: string,
): string | undefined {
  const value = source[field];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') {
    throw new ConsoleConfigError(`${where} has a non-string optional field "${field}".`);
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * `label`, when present, must be a non-empty string.
 *
 * Deliberately STRICTER than `optionalString`, which reads a blank value as
 * absent. That tolerance is right for `tone` and `customerEnvironmentId`, where a
 * blank and a missing value mean the same thing; it is wrong here, because the
 * generator either omits `label` on purpose (to keep customer names out of a
 * public file) or emits one — and a blank one is the generator failing, which
 * should be loud rather than quietly rendered as the id.
 */
function optionalLabel(source: Record<string, unknown>, where: string): string | undefined {
  const value = source.label;
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string' || value.trim() === '') {
    throw new ConsoleConfigError(`${where} has an empty or non-string "label".`);
  }
  return value.trim();
}

function optionalHttpsUrl(
  source: Record<string, unknown>,
  field: string,
  where: string,
): string | undefined {
  const value = optionalString(source, field, where);
  if (value === undefined) return undefined;
  if (!/^https:\/\//i.test(value)) {
    throw new ConsoleConfigError(`${where} field "${field}" must be an absolute https URL (got "${value}").`);
  }
  return value;
}

/**
 * The optional `signInHelper` block. Present-but-malformed fails loudly, like every
 * other field: a store link that is not https is a broken generator, not a value
 * to render as a link an operator will click.
 */
function parseSignInHelper(raw: unknown, where: string): SignInHelperLinks | undefined {
  if (raw === undefined || raw === null) return undefined;
  const at = `${where} signInHelper`;
  if (!isRecord(raw)) {
    throw new ConsoleConfigError(`${at} is not an object.`);
  }
  const extensionId = optionalString(raw, 'extensionId', at);
  if (extensionId !== undefined && !EXTENSION_ID_PATTERN.test(extensionId)) {
    throw new ConsoleConfigError(`${at} field "extensionId" must be a 32-character a-p extension ID (got "${extensionId}").`);
  }
  const links: SignInHelperLinks = {
    edgeStoreUrl: optionalHttpsUrl(raw, 'edgeStoreUrl', at),
    chromeStoreUrl: optionalHttpsUrl(raw, 'chromeStoreUrl', at),
    downloadUrl: optionalHttpsUrl(raw, 'downloadUrl', at),
    forceInstallUpdateUrl: optionalHttpsUrl(raw, 'forceInstallUpdateUrl', at),
    extensionId,
  };
  // A forcelist entry is `<id>;<url>`: one without the other is a broken generator,
  // and rendering half of a policy value would hand IT something that silently fails.
  if (Boolean(links.forceInstallUpdateUrl) !== Boolean(links.extensionId)) {
    throw new ConsoleConfigError(`${at} must set "forceInstallUpdateUrl" and "extensionId" together.`);
  }
  // Absent keys are dropped rather than carried as undefined, so an equality check on
  // the parsed block sees exactly what the file said.
  const present = Object.fromEntries(
    Object.entries(links).filter(([, v]) => v !== undefined),
  ) as SignInHelperLinks;
  return Object.keys(present).length > 0 ? present : undefined;
}

function parseEnvironment(raw: unknown, index: number, where: string): ConsoleEnvironment {
  const at = `${where} environments[${index}]`;
  if (!isRecord(raw)) {
    throw new ConsoleConfigError(`${at} is not an object.`);
  }

  const managerBaseUrl = requireString(raw, 'managerBaseUrl', at);
  if (!/^https?:\/\//i.test(managerBaseUrl)) {
    throw new ConsoleConfigError(
      `${at} field "managerBaseUrl" must be an absolute http(s) URL (got "${managerBaseUrl}").`,
    );
  }

  return {
    id: requireString(raw, 'id', at),
    // Optional (FD-586), but still validated when present: a label that is an
    // empty string or a number is a broken generator, not an omitted field, and
    // treating it as absent would hide the defect behind the id fallback.
    label: optionalLabel(raw, at),
    // Normalised, not rejected: `api/client.ts` builds `${base}${path}` where
    // path already leads with `/v1`, so a trailing slash yields `//v1` and a
    // wall of 404s from the Manager. There is exactly one correct reading of a
    // trailing slash, so silently fixing it is safe — where a value is absent
    // or unusable we fail loudly instead.
    managerBaseUrl: managerBaseUrl.replace(/\/+$/, ''),
    customerEnvironmentId: optionalString(raw, 'customerEnvironmentId', at),
    tone: optionalString(raw, 'tone', at),
  };
}

/**
 * Validate an already-parsed object into a `ConsoleConfig`.
 *
 * Exported because it IS the schema: FD-423's generator must produce something
 * this function accepts, and `frontend/public/config.example.json` is the shared
 * fixture both sides check against — one shape, not two copies of it.
 *
 * @param where Prefix for every message, so a DEV-fallback problem and a
 *              `config.json` problem never read as each other.
 */
export function parseConsoleConfig(raw: unknown, where = 'config.json'): ConsoleConfig {
  if (!isRecord(raw)) {
    throw new ConsoleConfigError(`${where} is not a JSON object.`);
  }

  const schemaVersion = raw.schemaVersion;
  if (typeof schemaVersion !== 'number') {
    throw new ConsoleConfigError(`${where} is missing required field "schemaVersion".`);
  }
  if (schemaVersion !== CONSOLE_CONFIG_SCHEMA_VERSION) {
    throw new ConsoleConfigError(
      `${where} declares schemaVersion ${schemaVersion}; this console understands ` +
        `${CONSOLE_CONFIG_SCHEMA_VERSION}. The console and its configuration were ` +
        `deployed from different revisions.`,
    );
  }

  const environments = raw.environments;
  if (!Array.isArray(environments) || environments.length === 0) {
    throw new ConsoleConfigError(
      `${where} field "environments" must be a non-empty array — a console with no ` +
        `environment has nothing to sign in to.`,
    );
  }

  const parsed = environments.map((entry, index) => parseEnvironment(entry, index, where));

  // FD-421 resolves `?env=<id>` against this list, so two entries sharing an id
  // is not a cosmetic duplicate — it is a selector that cannot resolve, and
  // reaching the wrong environment is the hazard that whole design guards against.
  const seen = new Set<string>();
  for (const environment of parsed) {
    if (seen.has(environment.id)) {
      throw new ConsoleConfigError(
        `${where} has two environments with id "${environment.id}"; ids must be unique.`,
      );
    }
    seen.add(environment.id);
  }

  return {
    schemaVersion,
    clientId: requireString(raw, 'clientId', where),
    authority: requireString(raw, 'authority', where),
    scope: requireString(raw, 'scope', where),
    redirectUri: optionalString(raw, 'redirectUri', where),
    environments: parsed,
    signInHelper: parseSignInHelper(raw.signInHelper, where),
  };
}

/**
 * Rebuild the pre-FD-420 compiled-in configuration from `VITE_*`.
 *
 * DEV ONLY. This is what keeps `.env.local` and the `vite.config.ts` vitest env
 * block working unchanged, and it is the ONLY surviving read of
 * `import.meta.env.VITE_*` on the boot path.
 *
 * The three throws below preserve the diagnostics `authConfig.ts` used to raise
 * at module load — same causes, same `.env.local` pointer, but now routed to
 * `BootFailure` instead of a throw that fired before anything could render.
 */
function devFallbackConfig(): ConsoleConfig {
  const where = 'The DEV VITE_* fallback';
  const env = import.meta.env;

  if (!env.VITE_CLIENT_ID || !env.VITE_TENANT_ID) {
    throw new ConsoleConfigError(
      `${where} is missing VITE_CLIENT_ID or VITE_TENANT_ID. Create frontend/.env.local ` +
        `with these values (Vite does not load .env.staging in dev mode).`,
    );
  }
  if (!env.VITE_API_SCOPE) {
    throw new ConsoleConfigError(
      `${where} is missing VITE_API_SCOPE (the admin API scope). Set it to ` +
        `api://<app-registration-id>/AdminAccess in frontend/.env.local.`,
    );
  }

  return parseConsoleConfig(
    {
      schemaVersion: CONSOLE_CONFIG_SCHEMA_VERSION,
      clientId: env.VITE_CLIENT_ID,
      // Built here, and ONLY here, from a tenant id. Everywhere else the
      // authority arrives whole, so Mode 2's `.../organizations` needs no code.
      authority: `https://login.microsoftonline.com/${env.VITE_TENANT_ID}`,
      scope: env.VITE_API_SCOPE,
      redirectUri: env.VITE_REDIRECT_URI || undefined,
      environments: [
        {
          id: 'local',
          label: 'Local development',
          // In DEV `api/client.ts` sends relative URLs so the Vite proxy makes
          // the hop, which leaves this value informational. The dev origin is
          // the honest answer when nothing is configured — that IS where the
          // requests go before the proxy forwards them.
          managerBaseUrl:
            env.VITE_MANAGER_API_BASE_URL ||
            (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'),
        },
      ],
    },
    where,
  );
}

export interface LoadConsoleConfigOptions {
  /**
   * Whether the DEV fallback is permitted. Defaults to `import.meta.env.DEV`.
   * Injected rather than read inline so a test can exercise BOTH postures
   * deterministically — the production posture is the one that matters, and it
   * is the one a DEV-mode test runner cannot otherwise reach.
   */
  dev?: boolean;
  /** Injected for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}

function looksLikeHtml(body: string, contentType: string | null | undefined): boolean {
  if (contentType && /text\/html/i.test(contentType)) return true;
  return body.trimStart().startsWith('<');
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  return typeof err === 'string' ? err : 'unknown error';
}

/**
 * Fetch, validate and return the console configuration.
 *
 * Production has NO fallback, and that is the decision this function exists to
 * enforce. A bundle that silently booted against a compiled-in host when
 * `config.json` was missing would point an operator's console at the wrong
 * environment without telling them — exactly the hazard FD-ADR-040's selector,
 * badge and destructive-dialog echo are built to prevent. Failing loudly at boot
 * is strictly better than a console that works and lies.
 */
export async function loadConsoleConfig(
  options: LoadConsoleConfigOptions = {},
): Promise<ConsoleConfig> {
  const dev = options.dev ?? import.meta.env.DEV;
  const doFetch = options.fetchImpl ?? fetch;

  let response: Response;
  try {
    // `no-store`: a cached config is a console pointed at last week's fleet.
    response = await doFetch(CONSOLE_CONFIG_URL, { cache: 'no-store' });
  } catch (err) {
    if (dev) return devFallbackConfig();
    throw new ConsoleConfigError(
      `${CONSOLE_CONFIG_URL} could not be fetched (${describe(err)}). The console reads ` +
        `every value it needs — clientId, authority, scope and the environment list — ` +
        `from this file at runtime; none of it is compiled into the bundle.`,
    );
  }

  if (!response.ok) {
    if (dev) return devFallbackConfig();
    throw new ConsoleConfigError(
      `${CONSOLE_CONFIG_URL} could not be loaded (HTTP ${response.status}). Deploy it ` +
        `alongside the bundle — the console reads clientId, authority, scope and the ` +
        `environment list from it at runtime.`,
    );
  }

  const body = await response.text();

  // Azure Static Web Apps rewrites an unmatched path to index.html and answers
  // 200, not 404 (`staticwebapp.config.json` responseOverrides). So a config.json
  // that was never deployed arrives here as a page of HTML with a SUCCESS status,
  // and the naive failure is `Unexpected token '<'`, which names nothing. Detect
  // it explicitly and say what actually happened.
  if (looksLikeHtml(body, response.headers?.get?.('content-type'))) {
    if (dev) return devFallbackConfig();
    throw new ConsoleConfigError(
      `${CONSOLE_CONFIG_URL} returned HTML, not JSON. On Azure Static Web Apps an ` +
        `undeployed file is rewritten to index.html with HTTP 200, so this almost ` +
        `always means config.json was never uploaded with the bundle.`,
    );
  }

  let raw: unknown;
  try {
    raw = JSON.parse(body);
  } catch (err) {
    // Deliberately NOT dev-fallbackable: the file is present and malformed.
    // Falling back would hide a broken deployment behind a console that appeared
    // to work — and in DEV that is the exact moment someone is editing the file.
    throw new ConsoleConfigError(`${CONSOLE_CONFIG_URL} is not valid JSON (${describe(err)}).`);
  }

  return parseConsoleConfig(raw);
}
